/**
 * useClarifyingQuestions - Polls the LLM for short clarifying questions
 * a clinician should ask the patient, while a ChartMind encounter is being
 * recorded. Refreshes every POLL_INTERVAL_MS so the visible list stays
 * relevant as the transcript grows.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { invokeLLMPrompt } from "services/llmService";
import { useAuth } from "hooks/useAuth";

const QUERY_ID = "chartmind-clarifying-questions";
const POLL_INTERVAL_MS = 10000;
const MIN_TRANSCRIPT_LENGTH = 50;

const cleanQuestion = (raw) => {
  if (typeof raw === "string") return raw.trim();
  if (raw && typeof raw === "object") {
    return (raw.question || raw.text || raw.prompt || "").toString().trim();
  }
  return "";
};

const QUESTION_KEYS = [
  "clarifying_questions",
  "clarifyingQuestions",
  "questions",
  "clarifications",
  "suggestedQuestions",
  "suggested_questions",
];

const cleanList = (arr) =>
  Array.isArray(arr) ? arr.map(cleanQuestion).filter(Boolean) : [];

// LLMs sometimes prepend prose before the JSON object. Extract the JSON
// candidate by preferring a fenced ```json``` block; otherwise grab
// everything between the first '{' and the last '}'.
const extractJsonCandidate = (text) => {
  const fenced = text.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (fenced) return fenced[1].trim();

  const first = text.indexOf("{");
  const last = text.lastIndexOf("}");
  if (first !== -1 && last > first) return text.slice(first, last + 1);

  return null;
};

const parseQuestions = (output) => {
  if (!output) return [];
  const trimmed = output.trim();

  const jsonStr = extractJsonCandidate(trimmed);
  if (jsonStr) {
    try {
      const parsed = JSON.parse(jsonStr);

      if (Array.isArray(parsed)) {
        const cleaned = cleanList(parsed);
        if (cleaned.length > 0) return cleaned;
      }

      if (parsed && typeof parsed === "object") {
        for (const key of QUESTION_KEYS) {
          const cleaned = cleanList(parsed[key]);
          if (cleaned.length > 0) return cleaned;
        }
      }
    } catch (err) {
      console.warn(
        "[Clarifying Questions] JSON parse failed, falling back to line parsing:",
        err?.message || err,
      );
    }
  }

  return trimmed
    .split("\n")
    .map((line) => line.replace(/^[\s\-*\d.)]+/, "").trim())
    .filter((line) => line.length > 0 && line.endsWith("?"));
};

const useClarifyingQuestions = ({ transcript, isRecording, enabled = true }) => {
  const { organizationId } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const transcriptRef = useRef(transcript);
  const lastQueriedTranscriptRef = useRef("");
  const inFlightRef = useRef(false);

  useEffect(() => {
    transcriptRef.current = transcript;
  }, [transcript]);

  const fetchQuestions = useCallback(async () => {
    const current = (transcriptRef.current || "").trim();
    if (current.length < MIN_TRANSCRIPT_LENGTH) {
      console.log("[Clarifying Questions] skip — transcript too short", {
        length: current.length,
        min: MIN_TRANSCRIPT_LENGTH,
      });
      return;
    }
    if (current === lastQueriedTranscriptRef.current) {
      console.log("[Clarifying Questions] skip — transcript unchanged");
      return;
    }
    if (inFlightRef.current) {
      console.log("[Clarifying Questions] skip — request already in flight");
      return;
    }

    inFlightRef.current = true;
    lastQueriedTranscriptRef.current = current;
    setLoading(true);
    setError(null);

    console.log("[Clarifying Questions] → firing request", {
      transcriptLength: current.length,
      transcriptPreview: current.slice(0, 120),
    });

    try {
      const result = await invokeLLMPrompt({
        promptId: QUERY_ID,
        userMessage: `PATIENT ENCOUNTER TRANSCRIPT:\n\n${current}`,
        organizationId,
      });
      const rawOutput = result?.output;
      console.log("[Clarifying Questions] ← raw LLM response:\n", rawOutput);
      console.log("[Clarifying Questions] response meta:", result?.meta || null);

      const parsed = parseQuestions(rawOutput);
      console.log("[Clarifying Questions] parsed questions:", parsed);

      if (parsed.length > 0) {
        setQuestions(parsed);
      } else {
        console.warn(
          "[Clarifying Questions] parser returned 0 questions — keeping previous list",
        );
      }
    } catch (err) {
      console.error("[Clarifying Questions] error:", err);
      setError(err?.message || "Failed to generate clarifying questions");
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    if (!isRecording || !enabled) {
      console.log(
        "[Clarifying Questions] polling disabled",
        { isRecording, enabled },
      );
      return undefined;
    }
    fetchQuestions();
    const id = setInterval(fetchQuestions, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isRecording, enabled, fetchQuestions]);

  // When recording stops, allow a fresh fetch on the next session without
  // throwing away the questions already on screen.
  useEffect(() => {
    if (!isRecording) lastQueriedTranscriptRef.current = "";
  }, [isRecording]);

  const clear = useCallback(() => {
    setQuestions([]);
    setError(null);
    lastQueriedTranscriptRef.current = "";
  }, []);

  return { questions, loading, error, clear };
};

export default useClarifyingQuestions;
